import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AuthModal({ open, onClose, reason }) {
  const { login, register, googleLogin } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isWall = reason === "wall";

  useEffect(() => { if (open && isWall) setMode("register"); }, [open, isWall]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      mode === "login" ? await login(email, password) : await register(email, password, name);
      onClose(); setEmail(""); setPassword(""); setName("");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? d.map(e => e.msg).join(" ") : "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 text-dark max-w-md rounded-2xl p-0 gap-0 overflow-hidden">
        {/* Gradient header */}
        <div className="h-2 bg-gradient-to-r from-view-a via-purple-500 to-view-b" />
        <div className="p-6 pt-5">
          <DialogHeader className="mb-1">
            <DialogTitle className="font-heading text-3xl tracking-tight text-center">
              {isWall ? "KEEP YOUR STREAK GOING" : mode === "login" ? "WELCOME BACK" : "JOIN THE CONVERSATION"}
            </DialogTitle>
            <DialogDescription className="text-center text-neutral text-sm">
              {isWall ? "You've cast 3 votes as a guest. Create a free account to keep voting — your votes carry over." : mode === "login" ? "Ready to take your side?" : "Read both sides. Then make your call."}
            </DialogDescription>
          </DialogHeader>

          <button onClick={googleLogin} className="w-full mt-4 py-3 px-4 border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center gap-3 text-sm font-semibold rounded-xl transition-colors shadow-sm" data-testid="google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-neutral font-medium">OR</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-medium text-neutral">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-xl border-gray-200 focus:ring-view-a" placeholder="Your name" data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-neutral">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-xl border-gray-200 focus:ring-view-a" placeholder="you@email.com" data-testid="auth-email-input" />
            </div>
            <div>
              <Label className="text-xs font-medium text-neutral">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-xl border-gray-200 focus:ring-view-a" placeholder="Password" data-testid="auth-password-input" />
            </div>
            {error && <p className="text-sm text-view-b font-medium" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full py-3 bg-view-a text-white font-semibold rounded-xl hover:bg-view-a-dark transition-all shadow-sm disabled:opacity-50" data-testid="auth-submit-btn">
              {loading ? "..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="text-center text-sm text-neutral mt-4">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-view-a font-semibold hover:underline" data-testid="auth-toggle-btn">
              {mode === "login" ? "Join the conversation" : "Sign in"}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
